/**
 * Access-log wire contract (PRD H4 "access log queryable"; increment-9 plan
 * §1 PR3).
 *
 * GET /api/episodes/:episodeId/access-log — every audited act that reached
 * this episode, newest first: rows keyed to the episode itself (migration
 * 018) UNION rows keyed to any of the episode's sessions. No adopted spec
 * depicts a screen for this, so this contract plus the RUNBOOK §5.1 SQL set
 * IS the queryability surface (plan §0.1a).
 *
 * PAGINATION IS KEYSET, BY ROW ID, DELIBERATELY. Ids are writer-minted
 * UUIDv7 (ADR-0010 §6) and `occurred_at` is server-now at insert for every
 * writer, so id order IS insertion order. An offset would drift under a
 * reader's feet: reading the log appends `access-log.read` rows to the very
 * log being read (TM-08's rule — a read of an audit surface is itself an
 * access), and new rows only ever prepend under `id <` paging, never shift it.
 *
 * `action` is an OPEN vocabulary, not an enum: the log outlives any one
 * increment's action set (018's header lists today's words; PR5/PR6 add
 * more), and a filter that refused historical words would make old rows
 * unreachable — the opposite of an audit surface.
 *
 * THE CLINIC WORDS (increment-12, ADR-0027/0028/0030). A LIST, NOT AN ENUM,
 * for the reason directly above — it is written down so that every phase of
 * the increment writes the same word for the same act, not so that anything
 * validates against it:
 *
 *  - `clinic.create` — a clinic signed itself up.
 *  - `clinic.agree` — the clinic terms were agreed to, at a named version.
 *    Separate from `clinic.create` because the agreement is the legal event
 *    and must be answerable on its own.
 *  - `clinic.approve` — an operator approved the clinic (ADR-0030 §3).
 *  - `clinic.profile` — the clinic's profile head was superseded by an edit.
 *  - `clinic.member.add` · `clinic.member.change` · `clinic.member.end` — a
 *    membership began, had its roles changed, or ended.
 *  - `clinic.invite.create` · `clinic.invite.claim` — an invitation was minted
 *    and later spent. The claim is what turns a stranger into a member, so it
 *    is logged apart from `clinic.member.add`.
 *  - `episode.grant` · `episode.grant.revoke` — a colleague was put on an
 *    episode, or taken off before the grant's own end (ADR-0028 §2).
 *  - `episode.reassign-all` — one clinician's whole caseload changed hands
 *    (ADR-0028 §4). ONE row for the act, not one per episode: the question an
 *    auditor asks is who moved a caseload and why.
 *  - `episode.adopt` · `episode.adopt-all` — an operator bound an episode
 *    enrolled before the clinic tables existed to the clinic whose active
 *    clinician enrolled it (ADR-0030 §6's addendum of 2026-09-08 — contingency
 *    only; the `adopt-episodes` CLI). BOTH a row per episode, keyed on the
 *    episode's chain root, AND one summary row for the act — unlike
 *    `episode.reassign-all`, because an auditor of one patient's record must
 *    find the adoption on that episode's own log.
 *  - `clinic.roster.read` · `clinic.adherence.read` — the two clinic reports
 *    were read. Reading a surface over patients is itself an access (TM-08's
 *    rule, the same one that makes reading the access log append to it).
 */

import { z } from 'zod';

export const accessLogQuerySchema = z.object({
  /** Exact-match filter on the action word (e.g. `artifact.presign-get`). */
  action: z.string().min(1).max(128).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** The `nextCursor` of the previous page: rows strictly older than this id. */
  cursor: z.uuid().optional(),
});
export type AccessLogQuery = z.infer<typeof accessLogQuerySchema>;

export const accessLogRowSchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor: z.string(),
  /** Set on session-scoped acts; null on episode-scoped and global rows. */
  sessionId: z.uuid().nullable(),
  /** Set on episode-scoped acts from 018 on; null before, and on session rows. */
  episodeId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  /** The writer's call-site facts, verbatim — never normalized after the fact. */
  detail: z.unknown().nullable(),
});
export type AccessLogRow = z.infer<typeof accessLogRowSchema>;

/** GET /api/episodes/:episodeId/access-log */
export const episodeAccessLogResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.uuid(),
  rows: z.array(accessLogRowSchema),
  /** Feed back as `?cursor=` for the next (older) page; null = log exhausted. */
  nextCursor: z.uuid().nullable(),
});
export type EpisodeAccessLogResponse = z.infer<typeof episodeAccessLogResponseSchema>;
